import { Injectable, Injector } from '@angular/core';

import { AppConfigService } from '../app-config.service';
import { SsoScopeService } from '../sso/sso-scope.service';
import { StorageBrowser } from '../storage.service';

import {
  ALL_DEPARTMENTS_ID,
  RolesEnum,
} from 'src/app/features/tests/features/constants';
import { DEFAULT_PAGES, SUPERVISOR_EMAILS } from 'src/app/features/user-management/services/constants';
import { ROLE_PERMISSIONS } from './ability.constants';
import { Action, Subject } from './ability.type';

/**
 * What one test is made of: the test itself, its scoring, notes, report and
 * recording. On the Dormo Direct lab a test sent back to its lab after algo3 is
 * read-only, so while it is open none of these can be changed.
 */
const OPEN_TEST_CONTENT = new Set<Subject>([
  Subject.TestRecord,
  Subject.Algo,
  Subject.Annotation,
  Subject.Analysis,
  Subject.TechNote,
  Subject.ScorerNote,
  Subject.Report,
  Subject.SignedReport,
  Subject.TestBackupFile,
]);

/** Changing actions; looking and generating a PDF are not. */
const CHANGING_ACTIONS = new Set<Action>([Action.Edit, Action.Create, Action.Delete, Action.All]);

@Injectable({ providedIn: 'root' })
export class AbilityService {
  public subjects = Subject;
  public actions = Action;
  private readonly key = 'user';
  private readonly abilitySet = new Set<string>();
  public role: RolesEnum;
  public userId: string;
  public email: string;
  public isLoggedIn: boolean = false;
  public isDepartmentVisible: boolean;
  public departmentId?: string;
  public isMainLab: boolean = false;

  /** Whether the test open in the Working Area is read-only here; see {@link setOpenTestReadOnly}. */
  private openTestReadOnly = false;

  /**
   * isSSO is sticky-derived: sessionStorage `sso_scope` (set sync at SSO
   * landing) is the primary source of truth, with `/me`'s `isSSO` as a
   * fallback. This means refresh-token rotation (which may drop the claim)
   * doesn't break the kiosk UI, and guards see the right value
   * synchronously before /me resolves.
   */
  get isSSO(): boolean {
    return this.ssoScope.isActive() || !!this.storage.get(this.key)?.isSSO;
  }

  get mainLabUrl(): string | null {
    return this.ssoScope.get()?.mainLabUrl ?? this.storage.get(this.key)?.mainLabUrl ?? null;
  }

  constructor(
    private storage: StorageBrowser,
    private ssoScope: SsoScopeService,
    private injector: Injector,
  ) {
    this.loadRoleFromStorage();
  }

  refresh() {
    this.loadRoleFromStorage();
  }

  private loadRoleFromStorage() {
    const user = this.storage.get(this.key);
    const role = (user?.role || RolesEnum.Viewer) as RolesEnum;
    // TODO: Implement JWT decoding and role/departmentId extraction

    // const token = this.storage.get('token'); // Get the raw JWT string
    // let role = RolesEnum.Viewer; // Default fallback
    // let departmentId = null;

    // if (token) {
    //   const decoded = this.decodeJwt(token);
    //   if (decoded) {
    //     // Adjust 'role' and 'departmentId' keys based on your actual JWT payload structure
    //     role = (decoded.role || decoded.userRole || RolesEnum.Viewer) as RolesEnum;
    //     departmentId = decoded.departmentId;
    //   }
    // }

    this.role = role;
    this.isLoggedIn = !!user;
    this.departmentId = user?.departmentId;
    this.email = user?.email;
    this.userId = user?.id;
    this.isMainLab = !!user?.isMainLab;
    // isSSO and mainLabUrl are computed via getters above (sticky from
    // sessionStorage); no field assignment needed here.
    // this.departmentId = departmentId;

    if (role == RolesEnum.OutsourceScorer) {
      this.isDepartmentVisible = false;
    } else {
      this.isDepartmentVisible = user?.departmentId == ALL_DEPARTMENTS_ID;
    }

    this.abilitySet.clear();

    const abilities = ROLE_PERMISSIONS[role] || [];
    for (const [action, subject] of abilities) {
      this.abilitySet.add(`${action}:${subject}`);
    }
  }

  /**
   * Set by the Working Area while it shows a test sent back to its lab, and
   * cleared when it closes: every check for changing that test's content then
   * answers no, wherever in the Working Area it is asked.
   */
  setOpenTestReadOnly(readOnly: boolean): void {
    this.openTestReadOnly = readOnly;
  }

  can(action: Action, subject: Subject): boolean {
    if (this.openTestReadOnly && CHANGING_ACTIONS.has(action) && OPEN_TEST_CONTENT.has(subject)) {
      return false;
    }
    return (
      this.abilitySet.has(`${action}:${subject}`) ||
      this.abilitySet.has(`${action}:${Subject.All}`) ||
      this.abilitySet.has(`${Action.All}:${subject}`) ||
      this.abilitySet.has(`${Action.All}:${Subject.All}`)
    );
  }

  cannot(action: Action, subject: Subject): boolean {
    return !this.can(action, subject);
  }

  getRole(): RolesEnum {
    return this.role;
  }

  canEdit(subject: Subject): boolean {
    return this.can(Action.Edit, subject);
  }
  canView(subject: Subject): boolean {
    return this.can(Action.View, subject);
  }

  canCreate(subject: Subject): boolean {
    return this.can(Action.Create, subject);
  }

  /**
   * Whether this user sees anything shipping-related: Super Admin, Tech Admin
   * and Admin only. Checked as EDIT, not view — a Viewer can view everything,
   * shipping included, and must not get shipping.
   */
  canUseShipping(): boolean {
    return this.canEdit(Subject.DeviceShipment);
  }

  /**
   * Whether this user sees the Easyship features — shipping devices out on a
   * trial, the Shipments tab, tracking, shipping settings and the address check:
   * a shipping role, on a deployment with Easyship switched on. Manual shipment
   * entry stays on {@link canUseShipping} alone.
   */
  canUseEasyship(): boolean {
    // Looked up on use, not injected: the config service needs HttpClient, and
    // this service is built in many places that never ask about Easyship.
    return this.canUseShipping() && this.injector.get(AppConfigService).isEasyshipEnabled();
  }

  /**
   * Whether Dormo Direct is offered here — the department switch and Direct
   * patients: on a lab that sends patients to the Direct lab. Who may create a
   * patient or edit a department is decided by those permissions as usual.
   */
  canUseDormotechDirect(): boolean {
    return this.injector.get(AppConfigService).isDirectSourceLab();
  }

  /**
   * The Dormo Direct lab's Labs list — adding labs and issuing their secrets:
   * on the Direct lab, for super admins only. It decides which labs may send
   * patients there, across every department.
   */
  canManageDirectLabs(): boolean {
    return this.injector.get(AppConfigService).isDirectLab() && this.role === RolesEnum.SuperAdmin;
  }

  /**
   * The Lab column and Lab filter of the patients and tests lists: on the Dormo
   * Direct lab, where patients are sent from other labs. Everyone who can read
   * those lists sees which lab each patient came from.
   */
  seesSourceLabs(): boolean {
    return this.injector.get(AppConfigService).isDirectLab();
  }

  /**
   * A patient's details and medical history. On the Dormo Direct lab, a Direct
   * patient's belong to the lab that sent them: they are changed there and
   * reach this lab on their own, so they are not edited here.
   */
  /** Sending a Direct patient to Dormotech Direct again: the lab's admins, as the server allows. */
  canRetryDirectTransfer(): boolean {
    return [RolesEnum.SuperAdmin, RolesEnum.TechAdmin, RolesEnum.Admin].includes(this.role);
  }

  canEditPatientDetailsOf(patientType: string | null | undefined): boolean {
    return !(this.injector.get(AppConfigService).isDirectLab() && patientType === 'DIRECT');
  }

  /**
   * Connecting this lab to Dormo Direct with the secret from its Labs list: the
   * same people who may switch Direct on for a department.
   */
  canConnectDirect(): boolean {
    return this.canUseDormotechDirect() && this.canEdit(Subject.Department);
  }

  /**
   * Whether this user can copy tests between labs by hand from a test's
   * Transfer tab: super admins and tech admins, on any lab. The backend keeps a
   * tech admin to the tests of their own department.
   */
  canTransferTests(): boolean {
    return this.role === RolesEnum.SuperAdmin || this.role === RolesEnum.TechAdmin;
  }

  /**
   * Installing a package from a pasted link, and retrying such an install:
   * super admins only. A package says what the test becomes, results and all,
   * and nothing proves which lab built it.
   */
  canInstallTestPackages(): boolean {
    return this.role === RolesEnum.SuperAdmin;
  }

  canDelete(subject: Subject): boolean {
    return this.can(Action.Delete, subject);
  }

  isSupervisor(): boolean {
    if (!this.isLoggedIn) return false;
    return (
      SUPERVISOR_EMAILS
        .map((email) => email.toLowerCase())
        .includes(this.email.toLowerCase()) ||
      this.role === RolesEnum.OutsourceScorer
    );
  }

  isTechAdmin(): boolean {
    return this.role === RolesEnum.TechAdmin
  }

  defaultPage() {
    return DEFAULT_PAGES[this.role] || '/dashboard';
  }

  decodeJwt(token: string): any {
    try {
      const payload = token.split('.')[1]; // Extract the payload part of the JWT
      const decodedPayload = atob(payload); // Decode the Base64-encoded payload
      return JSON.parse(decodedPayload); // Parse the JSON payload
    } catch (error) {
      console.error('Invalid JWT token:', error);
      return null;
    }
  }
}
